import React from 'react';
import { Box, Typography, Select, MenuItem, Chip, Button } from '@mui/material';
import { NavigateNext as NavigateNextIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { DATE_RANGES, DashboardFilters } from '@hooks/useDashboardFilters';

const COLORS = {
  bg: '#0f1419', card: '#1a2332', cardBorder: '#2a3444',
  text: '#ffffff', muted: '#8b95a5', orange: '#FF6B4A', green: '#00D084',
};

interface Props {
  filters: DashboardFilters;
  onRefresh: () => void;
}

/**
 * The county/district/division/location + date-range filter bar shared by
 * every admin dashboard page (Dashboard, Reports, ...) — one implementation
 * so a selection means the same thing, and looks the same, everywhere.
 */
export const DashboardFilterBar: React.FC<Props> = ({ filters, onRefresh }) => {
  const {
    selectedCounty, selectedDistrict, selectedDivision, selectedLocation,
    selectCounty, selectDistrict, selectDivision, selectLocation, clear,
    dateRange, setDateRange,
    uniqueCounties, uniqueDistricts, uniqueDivisions, uniqueLocations,
  } = filters;

  return (
    <Box sx={{
      mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 1.5, flexWrap: 'wrap', p: 1,
      backgroundColor: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 0.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: selectedCounty ? COLORS.muted : COLORS.orange, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            NATIONAL
          </Typography>
          {[selectedCounty, selectedDistrict, selectedDivision, selectedLocation].filter(Boolean).map((crumb, i, arr) => (
            <Box key={crumb} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <NavigateNextIcon sx={{ fontSize: 14, color: COLORS.muted }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: i === arr.length - 1 ? 700 : 500, color: i === arr.length - 1 ? COLORS.orange : COLORS.muted, textTransform: 'uppercase' }}>
                {crumb}
              </Typography>
            </Box>
          ))}
          {!selectedCounty && (
            <>
              <NavigateNextIcon sx={{ fontSize: 14, color: COLORS.muted }} />
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted }}>All counties</Typography>
            </>
          )}
        </Box>

        {[
          { label: 'COUNTY', value: selectedCounty, setter: selectCounty, options: uniqueCounties, placeholder: 'All counties' },
          { label: 'SUBCOUNTY', value: selectedDistrict, setter: selectDistrict, options: uniqueDistricts, placeholder: 'All subcounties' },
          { label: 'DIVISION', value: selectedDivision, setter: selectDivision, options: uniqueDivisions, placeholder: 'Pick a subcounty' },
          { label: 'LOCATION', value: selectedLocation, setter: selectLocation, options: uniqueLocations, placeholder: 'Pick a division' },
        ].map((f) => (
          <Box key={f.label} sx={{ minWidth: 130 }}>
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.muted, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', mb: 0.25, pl: 0.5 }}>
              {f.label}
            </Typography>
            <Select
              size="small"
              value={f.value}
              displayEmpty
              onChange={(e) => f.setter(e.target.value)}
              sx={{
                backgroundColor: COLORS.bg, color: COLORS.text, fontSize: '0.8rem', height: 32, width: '100%',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.cardBorder },
                '& .MuiSelect-select': { py: 0.5 },
              }}
            >
              <MenuItem value=""><Typography sx={{ fontSize: '0.8rem', color: COLORS.muted }}>{f.placeholder}</Typography></MenuItem>
              {f.options.map((opt: string) => (
                <MenuItem key={opt} value={opt}><Typography sx={{ fontSize: '0.8rem' }}>{opt}</Typography></MenuItem>
              ))}
            </Select>
          </Box>
        ))}

        {selectedCounty && (
          <Button size="small" onClick={clear} sx={{ color: COLORS.muted, textTransform: 'none', fontSize: '0.75rem', alignSelf: 'flex-end', mb: 0.25 }}>
            Clear
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {DATE_RANGES.map((r) => (
          <Chip
            key={r.label}
            label={r.label}
            onClick={() => setDateRange(r.label)}
            size="small"
            sx={{
              fontSize: '0.72rem', fontWeight: dateRange === r.label ? 700 : 500,
              backgroundColor: dateRange === r.label ? 'rgba(0, 208, 132, 0.18)' : COLORS.bg,
              color: dateRange === r.label ? COLORS.green : COLORS.muted,
              border: `1px solid ${dateRange === r.label ? COLORS.green : COLORS.cardBorder}`,
              px: 0.25,
              '&:hover': { backgroundColor: dateRange === r.label ? 'rgba(0, 208, 132, 0.28)' : COLORS.card },
              borderRadius: 0.75,
            }}
          />
        ))}
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={onRefresh}
          sx={{
            borderColor: COLORS.cardBorder, color: COLORS.muted, textTransform: 'none',
            fontSize: '0.75rem', height: 32, borderRadius: 0.75,
            '&:hover': { borderColor: COLORS.green, color: COLORS.green },
          }}
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );
};
